<script setup lang="ts">
import { Divider } from "~/components/clarity-ui/Divider";
import useMarketSetup from "~/composables/useMarketSetup";
import useMyBritaUser from "~/composables/useMyBritaUser";
import CommonLabel from "~/templates/components/CommonLabel.vue";
import LoadingElement from "~/templates/elements/LoadingElement.vue";
import SvgIcon from "~/templates/elements/SvgIcon.vue";

const { path } = useRoute();
const { marketSetupAdmin } = useMarketSetup();

const { isMagnoliaEdit } = useMagnoliaContext();

const { myBritaLogout, isMyBritaLoggedIn } = useMyBritaUser();

const onLogout = async () => await myBritaLogout();
</script>

<template>
  <div class="container grid grid-cols-12 justify-between gap-4 xl:gap-x-6">
    <LoadingElement
      v-if="!isMyBritaLoggedIn && !isMagnoliaEdit"
      class="col-span-full aspect-3/4 w-full lg:aspect-video"
    />
    <template v-else>
      <div class="col-span-full mt-12 md:order-2 md:col-span-9 xl:col-start-4">
        <slot />
      </div>
      <menu
        class="account-navi order-1 col-span-full mt-6 flex flex-col rounded-xl md:col-span-3 md:mt-12 xl:col-span-2 xl:mt-12"
      >
        <li class="px-4">
          <NuxtLink
            :to="marketSetupAdmin?.myBritaOverviewPage ?? ''"
            class="account-link text-icon-link text-black"
          >
            <SvgIcon class="h-5 w-5" name="Overview" />
            <CommonLabel class="truncate" path="BWF/shop/account/grid/overview" />
          </NuxtLink>
        </li>

        <li class="px-4">
          <NuxtLink
            :to="marketSetupAdmin?.myBritaProfilePage ?? ''"
            class="account-link text-icon-link text-black"
          >
            <SvgIcon class="h-5 w-5" name="User" />
            <CommonLabel class="truncate" path="BWF/shop/account/grid/myProfile" />
          </NuxtLink>
        </li>

        <li class="px-4">
          <NuxtLink
            :to="marketSetupAdmin?.myBritaAddressPage ?? ''"
            class="account-link text-icon-link text-black"
          >
            <SvgIcon class="h-5 w-5" name="Address-Book" />
            <CommonLabel path="BWF/shop/account/grid/addressBook" />
          </NuxtLink>
        </li>

        <li class="px-4">
          <NuxtLink
            :class="{
              'is-active-parent':
                marketSetupAdmin?.myBritaOrderPage &&
                path.includes(marketSetupAdmin?.myBritaOrderPage),
            }"
            :to="marketSetupAdmin?.myBritaOrderPage ?? ''"
            class="account-link text-icon-link text-black"
          >
            <SvgIcon class="h-5 w-5" name="Orders" />
            <CommonLabel class="truncate" path="BWF/shop/account/grid/orderHistory" />
          </NuxtLink>
        </li>

        <li class="px-4">
          <NuxtLink
            :to="marketSetupAdmin?.myBritaSubscriptionPage ?? ''"
            class="account-link text-icon-link text-black"
          >
            <SvgIcon class="h-5 w-5 truncate" name="Subscription" />
            <CommonLabel class="truncate" path="BWF/shop/account/grid/subscriptions" />
          </NuxtLink>
        </li>

        <li v-if="marketSetupAdmin?.myBritaProductsPage" class="px-4">
          <NuxtLink
            :to="marketSetupAdmin?.myBritaProductsPage ?? ''"
            class="account-link text-icon-link text-black"
          >
            <SvgIcon class="h-5 w-5 truncate" name="Product" />
            <CommonLabel class="truncate" path="BWF/shop/account/grid/products" />
          </NuxtLink>
        </li>

        <li>
          <Divider class="my-3" />
        </li>

        <li class="px-4">
          <button class="account-link text-icon-link text-black" @click="onLogout()">
            <SvgIcon class="h-5 w-5" name="Logout" />
            <CommonLabel path="BWF/shop/account/grid/logout" />
          </button>
        </li>
      </menu>
    </template>
  </div>
</template>

<style scoped lang="postcss">
.account-link {
  @apply relative gap-2 py-3;

  &.router-link-active,
  &.is-active-parent {
    @apply bg-pale-blue/30;

    &::before,
    &::after {
      @apply absolute h-full w-4 bg-pale-blue/30;

      content: "";
    }

    &::before {
      @apply -left-4 rounded-l-lg;
    }

    &::after {
      @apply -right-4 rounded-r-lg;
    }
  }
}
</style>
